import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { Visibility, VisibilityOff, Refresh } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import patientPortalService from '../../services/patientPortalService';
import { UserRole, RoleUtils } from '../../types/enums';

const CompactSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  padding: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2),
}));

const SectionHeaderWithIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  '& .MuiSvgIcon-root': {
    color: theme.palette.primary.main,
  },
}));

const SectionContent = styled(Box)(({ theme }) => ({
  '& > * + *': {
    marginTop: theme.spacing(2),
  },
}));

const PatientPortalPanel = ({ patient, role, onUpdate }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Use RoleUtils to check if user has admin or facility admin role
  const isAdmin = RoleUtils.hasRole(role, UserRole.ADMIN);
  const isFacilityAdmin = RoleUtils.hasRole(role, UserRole.FACILITY_ADMIN);
  const canModify = (isAdmin || isFacilityAdmin) && onUpdate;

  useEffect(() => {
    if (patient) {
      // Treat missing isPatientPortalEnabled field as false
      const portalEnabled = patient.isPatientPortalEnabled === true;
      setIsEnabled(portalEnabled);
      setEmail(patient.email || '');
      // Generate a random password if portal is not enabled
      if (!portalEnabled) {
        setPassword(generateSecurePassword());
      } else {
        setPassword(''); // Don't show password for existing users
      }
      
      // Clear any previous messages
      setError('');
      setSuccess('');
    }
  }, [patient]);

  // Handle when portal is disabled
  useEffect(() => {
    if (!isEnabled && patient?.firebaseUid) {
      // Portal was disabled, clear password and generate new one for future use
      setPassword(generateSecurePassword());
    }
  }, [isEnabled, patient?.firebaseUid]);

  const generateSecurePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    
    return password;
  };

  const validatePassword = (password) => {
    if (!password || password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleGeneratePassword = () => {
    setPassword(generateSecurePassword());
  };

  const handleUpdate = async () => {
    if (!patient?.id) return;

    // If disabling portal, show confirmation
    if (!isEnabled && patient?.firebaseUid) {
      const confirmed = window.confirm(
        'Are you sure you want to disable the patient portal? This will permanently delete the patient\'s portal account and all associated data. This action cannot be undone.'
      );
      if (!confirmed) {
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isEnabled) {
        // Check if patient has an email address
        if (!email.trim()) {
          setError('Email address is required to enable patient portal access.');
          return;
        }

        // If patient already has a portal account, just update the email if it changed
        if (patient.firebaseUid) {
          let hasChanges = false;
          
          // Check if email changed
          if (email.trim() !== patient.email) {
            await patientPortalService.updatePatientEmail(patient.id, email.trim());
            hasChanges = true;
          }
          
          // Check if password changed
          if (password.trim() && password.trim() !== '') {
            // Validate password for updates
            const passwordError = validatePassword(password.trim());
            if (passwordError) {
              setError(passwordError);
              return;
            }
            
            await patientPortalService.updatePatientPassword(patient.id, patient.firebaseUid, password.trim());
            hasChanges = true;
          }
          
          // Always ensure isPatientPortalEnabled is set to true when portal is enabled
          if (!patient.isPatientPortalEnabled) {
            await patientPortalService.updatePatientPortalStatus(patient.id, true, patient.firebaseUid);
            hasChanges = true;
          }
          
          if (hasChanges) {
            setSuccess('Patient portal updated successfully.');
          } else {
            setSuccess('No changes to update.');
          }
          
          // Update local state
          if (onUpdate) {
            onUpdate({
              ...patient,
              email: email.trim(),
              isPatientPortalEnabled: true
            });
          }
          
          // Update local state to reflect the change immediately
          setEmail(email.trim());
          setIsEnabled(true);
        } else {
          // Check if we should create a new portal account or re-enable an existing one
          let shouldCreateNew = true;
          let existingFirebaseUid = null;
          
          // If patient has an email, check if a portal account already exists for that email
          if (email.trim()) {
            try {
              const userExists = await patientPortalService.checkPatientPortalUserExists(email.trim());
              if (userExists && userExists.uid) {
                // A portal account exists for this email, we should re-enable it
                shouldCreateNew = false;
                existingFirebaseUid = userExists.uid;
              }
            } catch {
              console.log('Could not check for existing user, proceeding with new account creation');
            }
          }
          
          if (shouldCreateNew) {
            // Check if patient portal user already exists with this email
            const userExists = await patientPortalService.checkPatientPortalUserExists(email.trim());
            if (userExists) {
              setError('A patient portal user already exists for this email address.');
              return;
            }

            // Validate password for new portal account
            const passwordError = validatePassword(password);
            if (passwordError) {
              setError(passwordError);
              return;
            }

            // Create Firebase Auth user for patient portal
            const result = await patientPortalService.enablePatientPortal(
              patient.id,
              {
                ...patient,
                email: email.trim()
              },
              password
            );

            setSuccess(`Patient portal enabled successfully! Generated password: ${result.password}`);
            
            // Update local state
            if (onUpdate) {
              onUpdate({
                ...patient,
                isPatientPortalEnabled: true,
                email: email.trim(),
                firebaseUid: result.user.uid
              });
            }
            
            // Update local state to reflect the change immediately
            setIsEnabled(true);
            setEmail(email.trim());
          } else {
            // Re-enable existing portal account
            try {
              // Update the patient record to link with existing Firebase Auth user
              await patientPortalService.updatePatientPortalStatus(patient.id, true, existingFirebaseUid);
              
              // Update the firebaseUid in the patient record
              if (onUpdate) {
                onUpdate({
                  ...patient,
                  isPatientPortalEnabled: true,
                  email: email.trim(),
                  firebaseUid: existingFirebaseUid
                });
              }
              
              setSuccess('Patient portal re-enabled successfully using existing account.');
              
              // Update local state to reflect the change immediately
              setIsEnabled(true);
              setEmail(email.trim());
            } catch (error) {
              console.error('Error re-enabling existing portal account:', error);
              setError('Failed to re-enable existing portal account. Please try creating a new one.');
            }
          }
        }
      } else {
        // Disable patient portal
        if (patient.firebaseUid) {
          await patientPortalService.disablePatientPortal(patient.id, patient.firebaseUid);
        }
        
        // Always ensure isPatientPortalEnabled is set to false when portal is disabled
        if (patient.isPatientPortalEnabled !== false) {
          await patientPortalService.updatePatientPortalStatus(patient.id, false);
        }
        
        setSuccess('Patient portal disabled successfully.');
        
        // Update local state
        if (onUpdate) {
          onUpdate({
            ...patient,
            isPatientPortalEnabled: false,
            firebaseUid: null
          });
        }
        
        // Update local state to reflect the change immediately
        setIsEnabled(false);
      }
    } catch (error) {
      console.error('Error updating patient portal:', error);
      setError(error.message || 'Failed to update patient portal status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CompactSection>
      <SectionHeaderWithIcon>
        <PersonIcon />
        <Typography variant="h6">Patient Portal Access</Typography>
      </SectionHeaderWithIcon>
      
      <SectionContent>
        <FormControlLabel
          control={
            <Checkbox
              checked={isEnabled}
              onChange={(event) => setIsEnabled(event.target.checked)}
              disabled={!canModify}
            />
          }
          label="Enable Patient Portal"
        />
        
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Allow this patient to access their medical records and appointments through the patient portal
        </Typography>

        {!isEnabled && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Patient portal is disabled. Check the box above to enable portal access and configure settings.
          </Alert>
        )}

        {!isEnabled && patient?.firebaseUid && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Disabling the portal will remove the patient's portal account and all associated data.
          </Alert>
        )}

        {isEnabled && (
          <Box sx={{ 
            opacity: isEnabled ? 1 : 0.5,
            transition: 'opacity 0.2s ease-in-out'
          }}>
            <TextField
              fullWidth
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!canModify || !isEnabled}
              type="email"
              size="small"
            />
            
            {patient?.firebaseUid ? (
              <>
                <TextField
                  fullWidth
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={!canModify || !isEnabled}
                  type={showPassword ? 'text' : 'password'}
                  size="small"
                  placeholder="Enter new password to update"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Toggle password visibility">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            disabled={!canModify || !isEnabled}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Typography variant="caption" color="text.secondary" display="block">
                  Enter a new password to update the patient's portal account. Leave empty to keep the current password.
                </Typography>
                
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Password must be at least 8 characters long and contain lowercase, uppercase, and numeric characters.
                </Typography>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={!canModify || !isEnabled}
                  type={showPassword ? 'text' : 'password'}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Toggle password visibility">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            disabled={!canModify || !isEnabled}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Generate new password">
                          <IconButton
                            onClick={handleGeneratePassword}
                            edge="end"
                            size="small"
                            disabled={!canModify || !isEnabled}
                          >
                            <Refresh />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Typography variant="caption" color="text.secondary" display="block">
                  This password will be used to create the patient's portal account. They can change it after their first login.
                </Typography>
                
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Password must be at least 8 characters long and contain lowercase, uppercase, and numeric characters.
                </Typography>
              </>
            )}
          </Box>
        )}

        {!isAdmin && !isFacilityAdmin && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Only administrators and facility administrators can modify patient portal access
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        {canModify && (
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={loading || !isEnabled || !email.trim() || (isEnabled && !patient?.firebaseUid && !password.trim())}
            sx={{ mt: 2 }}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Updating...' : 'Update'}
          </Button>
        )}
      </SectionContent>
    </CompactSection>
  );
};

export default PatientPortalPanel;
